import { MeteoCityCard } from "@/components/MeteoCityCard";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Coins, Flame, Sparkles, LogOut, Gift, Trophy, Mail, Target, BadgeCheck, Loader2, Bell, BellOff, Check, Plus, SlidersHorizontal, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { PasswordCard } from "@/components/PasswordCard";
import { apiValidateRedoo, getInterests, setInterests as apiSetInterests } from "@/lib/api";
import { pushSupported, getPushState, enablePush, disablePush } from "@/lib/push";
import { ZODIAC_SIGNS, displayCat, catColor } from "@/lib/readoo";
import { BackButton } from "@/components/BackButton";

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout, setUser, refresh } = useAuth();
  const [validating, setValidating] = useState(false);
  const { gamificationEnabled: gami } = useSettings();

  useEffect(() => {
    document.title = "Profilo · Readoo";
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  // Scroll alla sezione interessi quando si arriva con #interessi (da "Per te")
  useEffect(() => {
    if (loading || !user) return;
    if (location.hash === "#interessi") {
      const t = setTimeout(() => {
        document.getElementById("interessi")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [location.hash, loading, user]);

  // Aggiorna i dati (inclusi i Readoo in sospeso) all'apertura della pagina.
  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !user) return null;

  const signMeta = ZODIAC_SIGNS.find((s) => s.key === user.zodiac_sign);
  const pending = user.pending_points || 0;

  const handleValidate = async () => {
    if (pending <= 0 || validating) return;
    setValidating(true);
    try {
      const res = await apiValidateRedoo();
      if (res.user) setUser(res.user);
      toast.success(`${res.validated} Readoo convalidati e aggiunti al saldo!`);
      if (res.level_up) {
        toast.success(`Complimenti! Hai raggiunto il Livello ${res.level_up.level} — ${res.level_up.name}. Da ora ogni articolo vale ${res.level_up.article_reward} Readoo.`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.message || "Errore durante la convalida");
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="max-w-[720px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <BackButton />
      <div className="flex items-center gap-4 mb-8">
        <span className="w-16 h-16 rounded-full bg-brandTertiary text-brand flex items-center justify-center font-serif font-bold text-2xl">
          {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
        </span>
        <div>
          <h1 className="font-serif text-3xl font-semibold text-onSurface">{user.name || "Lettore"}</h1>
          <p className="text-sm text-subtle flex items-center gap-1.5"><Mail size={13} /> {user.email}</p>
        </div>
      </div>

      {gami && (<>
      {/* Convalida Readoo del giorno */}
      <div data-testid="validate-redoo-card" className="mb-6 rounded-2xl border border-brand/40 bg-brandTertiary/40 p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-wider text-brand mb-1">Readoo di oggi da convalidare</p>
            <p data-testid="pending-redoo-value" className="font-serif text-3xl font-bold text-onSurface flex items-center gap-2">
              <Coins size={22} className="text-brand" /> {pending}
            </p>
          </div>
          <button
            onClick={handleValidate}
            disabled={pending <= 0 || validating}
            data-testid="validate-redoo-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {validating ? <Loader2 size={16} className="animate-spin" /> : <BadgeCheck size={16} />}
            {pending > 0 ? "Convalida i tuoi Readoo" : "Nulla da convalidare"}
          </button>
        </div>
        <p className="text-xs text-subtle mt-3">
          Convalida i Readoo guadagnati oggi <b className="text-onSurfaceSecondary">entro mezzanotte</b>: se non lo fai, i Readoo del giorno vengono persi. La streak resta comunque salva.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-8">
        <Stat icon={Coins} label="Readoo" value={user.points || 0} />
        <Stat icon={Flame} label="Streak" value={`${user.streak || 0} gg`} />
        <LevelStat user={user} />
      </div>
      </>)}

      <div className="bg-surfaceSecondary border border-hair rounded-2xl divide-y divide-hair overflow-hidden">
        <Row to="/il-mio-segno" icon={Sparkles} label="Il mio segno" value={signMeta ? `${signMeta.emoji} ${signMeta.name}` : "Non impostato"} testid="profile-sign" />
        {gami && <Row to="/missioni" icon={Target} label="Missioni" testid="profile-missioni" />}
        {gami && <Row to="/riscatti" icon={Gift} label="I miei riscatti" testid="profile-redemptions" />}
        <Row to="/coupon" icon={Ticket} label="Coupon e codici sconto" testid="profile-coupons" />
        {gami && <Row to="/premi" icon={Trophy} label="Catalogo premi" testid="profile-rewards" />}
      </div>

      <InterestsCard />
      <MeteoCityCard />

      {gami && <PushToggle />}
      <PasswordCard />

      <button
        onClick={async () => { await logout(); navigate("/"); }}
        data-testid="profile-logout"
        className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full border border-hair bg-surfaceSecondary text-bad hover:border-bad/50 transition-colors text-sm font-semibold"
      >
        <LogOut size={16} /> Esci
      </button>
    </div>
  );
}

function InterestsCard() {
  const { user, setUser } = useAuth();
  const [available, setAvailable] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selected, setSelected] = useState(() => new Set(user?.interests || []));
  const [selectedTags, setSelectedTags] = useState(() => new Set(user?.interest_tags || []));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    getInterests()
      .then((r) => {
        if (!alive) return;
        setAvailable(r.available || []);
        setAvailableTags(r.available_tags || []);
        setSelected(new Set(r.categories || []));
        setSelectedTags(new Set(r.tags || []));
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const persist = async (nextCats, nextTags) => {
    setSaving(true);
    try {
      const orderedCats = available.filter((c) => nextCats.has(c));
      const orderedTags = availableTags.map((t) => t.slug).filter((s) => nextTags.has(s));
      const res = await apiSetInterests(orderedCats, orderedTags);
      if (res.user && setUser) setUser(res.user);
      setSelected(new Set(res.categories || orderedCats));
      setSelectedTags(new Set(res.tags || orderedTags));
    } catch (e) {
      setSelected(new Set(selected));
      setSelectedTags(new Set(selectedTags));
      toast.error(e.message || "Impossibile salvare gli interessi");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (cat) => {
    if (saving) return;
    const next = new Set(selected);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    setSelected(next);
    persist(next, selectedTags);
  };

  const toggleTag = (slug) => {
    if (saving) return;
    const next = new Set(selectedTags);
    next.has(slug) ? next.delete(slug) : next.add(slug);
    setSelectedTags(next);
    persist(selected, next);
  };

  const chipClass = (on) =>
    `inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors disabled:opacity-60 ${
      on
        ? "bg-brand text-onBrand border-brand"
        : "bg-surface text-onSurfaceSecondary border-hair hover:border-brand/50 hover:text-brand"
    }`;

  return (
    <div id="interessi" data-testid="interests-card" className="mt-6 rounded-2xl border border-hair bg-surfaceSecondary p-5 scroll-mt-28">
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal size={18} className="text-brand" />
        <h2 className="text-base font-semibold text-onSurface">I tuoi interessi</h2>
        {saving && <Loader2 size={14} className="animate-spin text-subtle ml-1" />}
      </div>
      <p className="text-xs text-subtle mb-4">
        Tocca una categoria per seguirla o smettere di seguirla. Le modifiche si applicano subito al tuo feed{" "}
        <Link to="/per-te" className="text-brand font-medium hover:underline">Per te</Link>.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-subtle"><Loader2 size={16} className="animate-spin" /> Caricamento…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {available.map((cat) => {
              const on = selected.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggle(cat)}
                  disabled={saving}
                  data-testid={`interest-chip-${cat}`}
                  aria-pressed={on}
                  className={chipClass(on)}
                >
                  {on ? <Check size={13} /> : <Plus size={13} />}
                  <span className="inline-flex items-center gap-1.5">
                    {!on && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: catColor(cat) }} />}
                    {displayCat(cat)}
                  </span>
                </button>
              );
            })}
          </div>

          {availableTags.length > 0 && (
            <div className="mt-6 pt-5 border-t border-hair">
              <p className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand mb-1">Calcio</p>
              <p className="text-xs text-subtle mb-3">Segui la tua squadra</p>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((t) => {
                  const on = selectedTags.has(t.slug);
                  return (
                    <button
                      key={t.slug}
                      type="button"
                      onClick={() => toggleTag(t.slug)}
                      disabled={saving}
                      data-testid={`interest-team-${t.slug}`}
                      aria-pressed={on}
                      className={chipClass(on)}
                    >
                      {on ? <Check size={13} /> : <Plus size={13} />}
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PushToggle() {
  const [state, setState] = useState("off");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getPushState().then(setState); }, []);
  if (state === "unsupported") return null;
  const on = state === "on";
  const denied = state === "denied";
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setState("off"); toast.success("Notifiche disattivate"); }
      else { await enablePush(); setState("on"); toast.success("Notifiche attivate! Ti ricorderemo di convalidare i Readoo."); }
    } catch (e) { toast.error(e.message || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="push-toggle-card" className="mt-6 rounded-2xl border border-hair bg-surfaceSecondary p-5 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        {on ? <Bell size={20} className="text-brand" /> : <BellOff size={20} className="text-subtle" />}
        <div>
          <p className="text-sm font-semibold text-onSurface">Notifiche push</p>
          <p className="text-xs text-subtle">{denied ? "Bloccate dal browser: sbloccale dalle impostazioni del sito." : "Ricevi il promemoria per convalidare i Readoo e le breaking news. Bonus: +1 Readoo al giorno mentre sono attive (da convalidare)."}</p>
        </div>
      </div>
      <button
        onClick={toggle} disabled={busy || denied}
        data-testid="push-toggle-button"
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${on ? "border border-hair bg-surfaceTertiary text-onSurface hover:border-brand/50" : "bg-brand text-onBrand hover:bg-brandSecondary"}`}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : on ? <BellOff size={16} /> : <Bell size={16} />}
        {on ? "Disattiva" : "Attiva notifiche"}
      </button>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-surfaceSecondary border border-hair rounded-2xl p-4 text-center">
      <Icon size={18} className="text-brand mx-auto mb-2" />
      <p className="font-serif text-2xl font-bold text-onSurface">{value}</p>
      <p className="text-[11px] font-mono uppercase tracking-wider text-subtle mt-0.5">{label}</p>
    </div>
  );
}

function LevelStat({ user }) {
  const reading = user.validated_reading_redoo_lifetime || 0;
  const nextMin = user.next_level_min;
  const pct = nextMin ? Math.min(100, Math.round((reading / nextMin) * 100)) : 100;
  return (
    <Link
      to="/livelli"
      data-testid="profile-level-card"
      className="group bg-surfaceSecondary border border-hair rounded-2xl p-4 text-center hover:border-brand/60 hover:bg-surfaceTertiary/40 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand/50"
      aria-label={`Livello ${user.level || 1} ${user.level_name || ""}, apri i dettagli`}
    >
      <Trophy size={18} className="text-brand mx-auto mb-2 group-hover:scale-110 transition-transform" />
      <p className="font-serif text-2xl font-bold text-onSurface leading-none">{user.level || 1}</p>
      <p className="text-[11px] font-mono uppercase tracking-wider text-subtle mt-0.5 truncate">{user.level_name || "Livello"}</p>
      {nextMin ? (
        <>
          <div className="mt-2 h-1.5 w-full rounded-full bg-surfaceTertiary overflow-hidden">
            <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[10px] text-subtle mt-1 font-mono">{reading.toLocaleString("it-IT")} / {nextMin.toLocaleString("it-IT")}</p>
        </>
      ) : (
        <p className="text-[10px] text-brand mt-2 font-mono">MAX</p>
      )}
    </Link>
  );
}

function Row({ to, icon: Icon, label, value, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 px-5 py-4 hover:bg-surfaceTertiary/50 transition-colors">
      <Icon size={18} className="text-onSurfaceSecondary" />
      <span className="text-sm font-medium text-onSurface flex-1">{label}</span>
      {value && <span className="text-sm text-subtle">{value}</span>}
    </Link>
  );
}
